// mapControls.js - 处理地图控制相关功能

/**
 * 转换经纬度为度分秒格式
 * @param {number} decimal 十进制经纬度值
 * @param {string} type 类型，lat代表纬度，lng代表经度
 * @returns {string} 度分秒格式的经纬度
 */
export function convertToDMS(decimal, type) {
  const absolute = Math.abs(decimal)
  const degrees = Math.floor(absolute)
  const minutesNotTruncated = (absolute - degrees) * 60
  const minutes = Math.floor(minutesNotTruncated)
  const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2)
  
  const direction = type === 'lat' 
    ? (decimal >= 0 ? 'N' : 'S') 
    : (decimal >= 0 ? 'E' : 'W')
  
  return `${degrees}° ${minutes}' ${seconds}" ${direction}`
}

/**
 * 格式化航向
 * @param {number} cog 航向值
 * @returns {string} 格式化后的航向
 */
export function formatCog(cog) {
  if(cog === null||cog === undefined||cog >=360){
    return '0.0'
  }
  return cog.toFixed(1)
}

/**
 * 格式化航速
 * @param {number} sog 航速值
 * @returns {string} 格式化后的航速
 */
export function formatSog(sog) {
  return sog.toFixed(1)
}

/**
 * 切换全屏模式
 * @param {boolean} isFullscreen 当前是否是全屏状态
 * @returns {boolean} 切换后的全屏状态
 */
export function toggleFullscreen(isFullscreen) {
  if (!isFullscreen) {
    // 进入全屏
    const element = document.documentElement // 整个页面
    if (element.requestFullscreen) {
      element.requestFullscreen()
    } else if (element.mozRequestFullScreen) { // Firefox
      element.mozRequestFullScreen()
    } else if (element.webkitRequestFullscreen) { // Chrome, Safari, Opera
      element.webkitRequestFullscreen()
    } else if (element.msRequestFullscreen) { // IE/Edge
      element.msRequestFullscreen()
    }
    return true
  } else {
    // 退出全屏
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen()
    }
    return false
  }
}

/**
 * 检测当前是否是全屏模式
 * @returns {boolean} 是否处于全屏模式
 */
export function isInFullscreenMode() {
  return !!(document.fullscreenElement || 
            document.webkitFullscreenElement || 
            document.mozFullScreenElement || 
            document.msFullscreenElement)
}

/**
 * 定位到指定坐标
 * @param {Object} map Leaflet地图实例
 * @param {Array} position [latitude, longitude]数组
 * @param {number} zoom 缩放级别
 */
export function panToLocation(map, position, zoom) {
  if (map && Array.isArray(position) && position.length === 2) {
    map.setView(position, zoom || map.getZoom())
  }
}

/**
 * 定位到船舶
 * @param {Object} ship 船舶对象
 * @param {Array} center 传出参数，更新后的地图中心点
 * @param {number} zoom 传出参数，更新后的缩放级别
 * @returns {boolean} 是否成功定位
 */
export function locateShip(ship, center, zoom) {
  if (ship && ship.pos) {
    center[0] = ship.pos.latitude
    center[1] = ship.pos.longitude
    if (typeof zoom === 'object') {
      zoom.value = 14
    } else {
      // 如果zoom不是引用对象，我们无法修改其值
      console.warn('zoom参数不是引用对象，无法修改其值')
    }
    return true
  }
  return false
}

/**
 * 处理地图类型切换
 * @param {string} newType 新的地图类型
 * @param {Object} layerStatus 图层状态对象
 * @returns {Object} 更新后的layerStatus对象
 */
export function handleMapTypeChange(newType, layerStatus) {
  const typeToLayerMap = {
    normal: 'tianditu',
    terrain: 'dixingtu',
    satellite: 'weixingtu'
  }
  
  // 创建layerStatus的副本
  const newLayerStatus = { ...layerStatus }
  
  Object.keys(typeToLayerMap).forEach(mapType => {
    const layerName = typeToLayerMap[mapType]
    newLayerStatus[layerName] = (mapType === newType)
  })
  
  return newLayerStatus
}

/**
 * 创建图片转Base64功能
 * @param {string} imgUrl 图片URL
 * @returns {Promise<string>} 转换后的Base64字符串
 */
export function imageToBase64(imgUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')
    
    img.onload = function() {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
        const dataURL = canvas.toDataURL('image/png')
        resolve(dataURL)
      } catch(e) {
        console.error('转换图片为Base64失败', e)
        reject(e)
      }
    }
    
    img.onerror = function(err) {
      console.error('图片加载失败')
      reject(err)
    }
    
    img.src = imgUrl
  })
}

/**
 * 搜索船舶
 * @param {string} searchText 搜索文本
 * @param {Array} ships 船舶数据数组
 * @returns {Object|null} 找到的船舶对象，未找到则返回null
 */
export function searchShip(searchText, ships) {
  if (!searchText.trim() || !Array.isArray(ships)) {
    return null
  }
  
  const text = searchText.trim().toLowerCase()
  
  return ships.find(ship => 
    ship.userid && ship.userid.toString().includes(text) || 
    (ship.shipname && ship.shipname.toLowerCase().includes(text))
  ) || null
} 